import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useFieldTelemetryData } from '@indeed/jspl-questions';
import type { ScreenerQuestionContextQuestion } from '../../../__generated__/graphql-base-types';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { buildQuestionTelemetryConfig, emitQuestionInteractions } from './questionTelemetry';
import type { AnswerValue } from './screenerQuestionUtils';

export interface UseQuestionInteractionTelemetryParams {
    questions: ScreenerQuestionContextQuestion[];
    loadedAnswers: Record<string, AnswerValue>;
    iaUid: string | undefined;
    draftId: string | undefined;
    /** `'auto-apply-questions'` on the main spine, `'auto-apply-questions-edit'` on the side-trip. */
    page: string;
    /**
     * Only register fields with jspl (and only flush) once the page has actually loaded — no point
     * tracking questions the jobseeker never got a chance to see. `useMemo`'d config-building
     * already returns `[]` while `questions` is empty (the query is itself gated on `iaUid`), so
     * this is a belt-and-suspenders invariant rather than a behavior change today.
     */
    enabled: boolean;
}

/** `useLayoutEffect` is undefined during SSR; fall back to `useEffect`, which never actually runs there. */
const useCommittedEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Reads jspl's per-field telemetry — captured by every jspl question component into the
 * `FieldTelemetryProvider` mounted around this page (see `QuestionsPage`) — and turns it into
 * `IaQuestionInteraction` Signals events. Must be called below that provider.
 *
 * Returns a `flush` callback for the caller's own checkpoint (here, right after a successful save).
 * The hook additionally flushes on its own when the jobseeker leaves without saving —
 * `visibilitychange`/`pagehide` (the same pair `useRefetchOnPageResume` uses, for the same
 * webview/bfcache reasons, IAFE-2525/1455) — and once more on a real unmount (not a React
 * StrictMode dev-mode effect replay), to catch an in-app navigation away that fires neither (e.g. a
 * load failure routing to the next draft).
 *
 * `flush` never throws — telemetry must never change product behaviour — but it is not
 * fire-and-forget: `eventLogger.logQuestionInteraction` reports per-row success, and a row that
 * comes back `false` (or whose call throws) is retried on the next flush via a `Set` of accepted
 * row keys (`questionInteractionRowKey`), rather than being dropped. jspl's own `markAsLogged()` is
 * intentionally never called — it would permanently hide a still-unanswered question from `data`.
 */
export const useQuestionInteractionTelemetry = ({
    questions,
    loadedAnswers,
    iaUid,
    draftId,
    page,
    enabled,
}: UseQuestionInteractionTelemetryParams): (() => void) => {
    const eventLogger = useEventLogger();

    const config = useMemo(
        () => (enabled ? buildQuestionTelemetryConfig(questions, loadedAnswers) : []),
        [enabled, questions, loadedAnswers],
    );
    // `markAsLogged` (unused): jspl's own logged-flag would permanently hide an unanswered field
    // from `data`. `acceptedRowKeysRef` below is our content-aware replacement.
    const { data } = useFieldTelemetryData(config);

    // A snapshot of everything `flush` needs, committed after each render (but before paint) rather
    // than read live — see the doc comment above for why a plain "assign during render" ref isn't
    // enough to make the explicit and abandonment flush paths agree on the latest `data`.
    const snapshot = { eventLogger, data, questions, iaUid, draftId, page, enabled };
    const committedSnapshotRef = useRef(snapshot);
    useCommittedEffect(() => {
        committedSnapshotRef.current = snapshot;
    });

    const acceptedRowKeysRef = useRef<Set<string>>(new Set());

    const flush = useCallback(() => {
        const current = committedSnapshotRef.current;
        if (!current.enabled || !current.iaUid || !current.draftId || current.data.length === 0) {
            return;
        }

        const { acceptedKeys } = emitQuestionInteractions({
            eventLogger: current.eventLogger,
            data: current.data,
            questions: current.questions,
            iaUid: current.iaUid,
            draftId: current.draftId,
            page: current.page,
            alreadyAcceptedKeys: acceptedRowKeysRef.current,
        });
        acceptedKeys.forEach((key) => acceptedRowKeysRef.current.add(key));
    }, []);

    // A ref so the abandonment listeners (registered once, below) always call the latest `flush`.
    // `flush` itself never changes identity (empty dep array), but keeping the indirection matches
    // the committed-snapshot pattern and costs nothing.
    const flushRef = useRef(flush);
    flushRef.current = flush;

    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') flushRef.current();
        };
        const handlePageHide = () => flushRef.current();

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
            mountedRef.current = false;
            // React StrictMode immediately unmounts and remounts a fresh instance of this effect in
            // dev to surface missing cleanup; a microtask lets that remount happen first, so this
            // only flushes a departure that turns out to be real, on the instance whose draft it
            // actually belongs to (not the next instance's).
            queueMicrotask(() => {
                if (!mountedRef.current) flushRef.current();
            });
        };
    }, []);

    return flush;
};
